import { API_CONFIG, API_ENDPOINTS } from '../config/api.config';
import authService from '../auth/authService';
import { apiGet } from '../utils/apiClient';
import type {
  AuditTimelineEntry,
  AuditTrailDetail,
  AuditTrailExportFormat,
  AuditTrailListItem,
  AuditTrailSearchFilters,
  AuditTrailSearchMeta,
  AuditTrailSortField,
} from '../types/acknowledgementAuditTrail.types';

function toDisplayString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

interface ApiEnvelope<T> {
  success?: boolean;
  message?: string;
  count?: number;
  meta?: AuditTrailSearchMeta;
  data?: T;
}

function buildQueryString(params: Record<string, string | number | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || String(value).trim() === '') {
      return;
    }
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

function mapListItem(raw: Record<string, unknown>): AuditTrailListItem {
  return {
    id: Number(raw.id),
    studyId: Number(raw.studyId ?? raw.study_id),
    patientId:
      raw.patientId != null
        ? Number(raw.patientId)
        : raw.patient_id != null
          ? Number(raw.patient_id)
          : null,
    facilityId: Number(raw.facilityId ?? raw.facility_id),
    patientName: (raw.patientName ?? raw.patient_name) as string | null,
    patientDicomId: (raw.patientDicomId ?? raw.patient_dicom_id) as string | null,
    studyInstanceUid: (raw.studyInstanceUid ?? raw.study_instance_uid) as string | null,
    studyDescription: (raw.studyDescription ?? raw.study_description) as string | null,
    studyDisplayName: (raw.studyDisplayName ?? raw.study_display_name) as string | null,
    accessionNumber: (raw.accessionNumber ?? raw.accession_number) as string | null,
    orderingClinician: (raw.orderingClinician ?? raw.ordering_clinician) as string | null,
    criticalityBand: String(raw.criticalityBand ?? raw.criticality_band ?? ''),
    notificationStatus: String(raw.notificationStatus ?? raw.notification_status ?? ''),
    acknowledgementStatus: String(raw.acknowledgementStatus ?? raw.acknowledgement_status ?? ''),
    acknowledgedAt: (raw.acknowledgedAt ?? raw.acknowledged_at) as string | null,
    acknowledgedByName: (raw.acknowledgedByName ?? raw.acknowledged_by_name) as string | null,
    acknowledgementMethod: (raw.acknowledgementMethod ?? raw.acknowledgement_method) as
      | string
      | null,
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    currentEscalationLevel:
      raw.currentEscalationLevel != null
        ? Number(raw.currentEscalationLevel)
        : raw.current_escalation_level != null
          ? Number(raw.current_escalation_level)
          : null,
    escalationStatus: (raw.escalationStatus ?? raw.escalation_status) as string | null,
    eventCount:
      raw.eventCount != null
        ? Number(raw.eventCount)
        : raw.event_count != null
          ? Number(raw.event_count)
          : null,
  };
}

function mapTimelineEntry(raw: Record<string, unknown>): AuditTimelineEntry {
  return {
    id: String(raw.id ?? ''),
    notificationId: Number(raw.notificationId ?? raw.notification_id),
    instanceId:
      raw.instanceId != null
        ? Number(raw.instanceId)
        : raw.instance_id != null
          ? Number(raw.instance_id)
          : null,
    timestamp: String(raw.timestamp ?? raw.createdAt ?? raw.created_at ?? ''),
    timelineEventType: String(
      raw.timelineEventType ?? raw.timeline_event_type ?? 'DeliveryAttempt'
    ) as AuditTimelineEntry['timelineEventType'],
    eventType: (raw.eventType ?? raw.event_type) as string | undefined,
    eventCategory: (raw.eventCategory ?? raw.event_category) as string | undefined,
    displayStatus: (raw.displayStatus ?? raw.display_status) as string | undefined,
    actorType: (raw.actorType ?? raw.actor_type) as string | undefined,
    actor: (raw.actor as string) ?? undefined,
    reason: (raw.reason as string) ?? null,
    levelNumber:
      raw.levelNumber != null
        ? Number(raw.levelNumber)
        : raw.level_number != null
          ? Number(raw.level_number)
          : null,
    recipientName: (raw.recipientName ?? raw.recipient_name) as string | null,
    recipientRole: (raw.recipientRole ?? raw.recipient_role) as string | null,
    recipientPhone: (raw.recipientPhone ?? raw.recipient_phone) as string | null,
    notificationChannel: (raw.notificationChannel ?? raw.notification_channel) as string | null,
    deliveryStatus: (raw.deliveryStatus ?? raw.delivery_status) as string | null,
    retryCount:
      raw.retryCount != null
        ? Number(raw.retryCount)
        : raw.retry_count != null
          ? Number(raw.retry_count)
          : null,
    deliveryKind: (raw.deliveryKind ?? raw.delivery_kind) as string | null,
    isReminder: Boolean(raw.isReminder ?? raw.is_reminder),
    source: (raw.source as string) ?? undefined,
    metadata: (raw.metadata as Record<string, unknown>) ?? null,
  };
}

function mapDetail(raw: Record<string, unknown>): AuditTrailDetail {
  const study = (raw.study as Record<string, unknown>) ?? null;
  const patient = (raw.patient as Record<string, unknown>) ?? null;
  const notificationSummary = (raw.notificationSummary ?? raw.notification_summary) as Record<
    string,
    unknown
  >;
  const acknowledgementSummary = (raw.acknowledgementSummary ??
    raw.acknowledgement_summary) as Record<string, unknown>;
  const escalationSummary = (raw.escalationSummary ?? raw.escalation_summary) as Record<
    string,
    unknown
  > | null;
  const acknowledgedByUser = (acknowledgementSummary?.acknowledgedByUser ??
    acknowledgementSummary?.acknowledged_by_user) as Record<string, unknown> | null;
  const acknowledgedByContact = (acknowledgementSummary?.acknowledgedByContact ??
    acknowledgementSummary?.acknowledged_by_contact) as Record<string, unknown> | null;
  const currentContact = (escalationSummary?.currentContact ??
    escalationSummary?.current_contact) as Record<string, unknown> | null;

  return {
    study: study
      ? {
          id: Number(study.id),
          studyInstanceUid: String(study.studyInstanceUid ?? study.study_instance_uid ?? ''),
          isCritical: Boolean(study.isCritical ?? study.is_critical),
          criticalityBand: String(study.criticalityBand ?? study.criticality_band ?? ''),
          accessionNumber: (study.accessionNumber ?? study.accession_number) as string | null,
          studyDescription: (study.studyDescription ?? study.study_description) as string | null,
          studyDisplayName: (study.studyDisplayName ?? study.study_display_name) as string | null,
        }
      : null,
    patient: patient
      ? {
          id: Number(patient.id),
          name: String(patient.name ?? ''),
          patientDicomId: (patient.patientDicomId ?? patient.patient_dicom_id) as string | null,
          risVisitId: (patient.risVisitId ?? patient.ris_visit_id) as string | null,
        }
      : null,
    notificationSummary: {
      id: Number(notificationSummary.id),
      escalationInstanceId: Number(
        notificationSummary.escalationInstanceId ?? notificationSummary.escalation_instance_id
      ),
      facilityId: Number(notificationSummary.facilityId ?? notificationSummary.facility_id),
      status: String(notificationSummary.status ?? notificationSummary.notification_status ?? ''),
      publishedAt: (notificationSummary.publishedAt ??
        notificationSummary.notification_published_at) as string | null,
      criticalityBand: String(
        notificationSummary.criticalityBand ?? notificationSummary.criticality_band ?? ''
      ),
      orderingClinician: (notificationSummary.orderingClinician ??
        notificationSummary.ordering_clinician) as string | null,
      createdAt: String(notificationSummary.createdAt ?? notificationSummary.created_at ?? ''),
      updatedAt: (notificationSummary.updatedAt ?? notificationSummary.updated_at) as string | null,
    },
    acknowledgementSummary: {
      status: toDisplayString(
        acknowledgementSummary.status ?? acknowledgementSummary.acknowledgement_status
      ),
      acknowledgedByUserId:
        acknowledgementSummary.acknowledgedByUserId != null
          ? Number(acknowledgementSummary.acknowledgedByUserId)
          : acknowledgementSummary.acknowledged_by_user_id != null
            ? Number(acknowledgementSummary.acknowledged_by_user_id)
            : null,
      acknowledgedByUser: acknowledgedByUser
        ? {
            id: Number(acknowledgedByUser.id),
            fullName: (acknowledgedByUser.fullName ?? acknowledgedByUser.full_name) as
              | string
              | null,
            username: (acknowledgedByUser.username as string) ?? null,
            role: (acknowledgedByUser.role as string) ?? null,
          }
        : null,
      acknowledgedByContact: acknowledgedByContact
        ? {
            id: Number(acknowledgedByContact.id),
            name: (acknowledgedByContact.name as string) ?? null,
            role: (acknowledgedByContact.role as string) ?? null,
            mobileNumber: (acknowledgedByContact.mobileNumber ??
              acknowledgedByContact.mobile_number) as string | null,
            department: (acknowledgedByContact.department as string) ?? null,
          }
        : null,
      role: (acknowledgementSummary.role as string) ?? null,
      method: (acknowledgementSummary.method ?? acknowledgementSummary.acknowledgement_method) as
        | string
        | null,
      response: (acknowledgementSummary.response ??
        acknowledgementSummary.acknowledgement_response) as string | null,
      ip: (acknowledgementSummary.ip ?? acknowledgementSummary.acknowledgement_ip) as string | null,
      acknowledgedAt: (acknowledgementSummary.acknowledgedAt ??
        acknowledgementSummary.acknowledged_at) as string | null,
    },
    escalationSummary: escalationSummary
      ? {
          instanceId: Number(escalationSummary.instanceId ?? escalationSummary.instance_id),
          status: String(escalationSummary.status ?? ''),
          currentLevel: Number(
            escalationSummary.currentLevel ?? escalationSummary.current_level ?? 1
          ),
          startedAt: (escalationSummary.startedAt ?? escalationSummary.started_at) as string | null,
          levelStartedAt: (escalationSummary.levelStartedAt ??
            escalationSummary.level_started_at) as string | null,
          acknowledgedAt: (escalationSummary.acknowledgedAt ??
            escalationSummary.acknowledged_at) as string | null,
          reminderCountAtLevel: Number(
            escalationSummary.reminderCountAtLevel ?? escalationSummary.reminder_count_at_level ?? 0
          ),
          triggeredLevels: Array.isArray(escalationSummary.triggeredLevels)
            ? escalationSummary.triggeredLevels.map(Number)
            : Array.isArray(escalationSummary.triggered_levels)
              ? escalationSummary.triggered_levels.map(Number)
              : [],
          completedAt: (escalationSummary.completedAt ?? escalationSummary.completed_at) as
            | string
            | null,
          currentContact: currentContact
            ? {
                id: Number(currentContact.id),
                name: (currentContact.name as string) ?? null,
                role: (currentContact.role as string) ?? null,
                mobileNumber: (currentContact.mobileNumber ?? currentContact.mobile_number) as
                  | string
                  | null,
                department: (currentContact.department as string) ?? null,
              }
            : null,
        }
      : null,
  };
}

export async function searchAcknowledgementAuditTrail(
  filters: AuditTrailSearchFilters & {
    limit?: number;
    offset?: number;
    sortBy?: AuditTrailSortField;
    sortOrder?: 'ASC' | 'DESC';
  }
): Promise<{
  success: boolean;
  data: AuditTrailListItem[];
  count: number;
  meta?: AuditTrailSearchMeta;
  error?: string;
}> {
  try {
    const url = `${API_ENDPOINTS.ACKNOWLEDGEMENT_AUDIT_TRAIL.SEARCH}${buildQueryString({
      facilityId: filters.facilityId,
      patientId: filters.patientId,
      studyId: filters.studyId,
      clinician: filters.clinician,
      criticalityBand: filters.criticalityBand,
      acknowledgementStatus: filters.acknowledgementStatus,
      startDate: filters.startDate,
      endDate: filters.endDate,
      search: filters.search,
      limit: filters.limit,
      offset: filters.offset,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    })}`;
    const response = await apiGet<ApiEnvelope<Record<string, unknown>[]>>(url, {
      requireAuth: true,
    });
    const payload = response.data;
    if (payload?.success === false) {
      return {
        success: false,
        data: [],
        count: 0,
        error: payload.message || 'Failed to search acknowledgement audit trail',
      };
    }
    const rows = Array.isArray(payload?.data) ? payload.data.map(row => mapListItem(row)) : [];
    return {
      success: true,
      data: rows,
      count: Number(payload?.count ?? payload?.meta?.total ?? rows.length),
      meta: payload?.meta
        ? {
            ...payload.meta,
            referringClinicianSummary: Array.isArray(
              payload.meta.referringClinicianSummary ?? payload.meta.referring_clinician_summary
            )
              ? (payload.meta.referringClinicianSummary ?? payload.meta.referring_clinician_summary)
              : undefined,
          }
        : undefined,
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      count: 0,
      error:
        error instanceof Error ? error.message : 'Failed to search acknowledgement audit trail',
    };
  }
}

export async function getAcknowledgementAuditTrailDetail(
  id: number,
  facilityId: number
): Promise<{ success: boolean; data?: AuditTrailDetail; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.ACKNOWLEDGEMENT_AUDIT_TRAIL.DETAIL(id)}${buildQueryString({
      facilityId,
    })}`;
    const response = await apiGet<ApiEnvelope<Record<string, unknown>>>(url, { requireAuth: true });
    const payload = response.data;
    if (payload?.success === false || !payload?.data) {
      return {
        success: false,
        error: payload?.message || 'Failed to load acknowledgement audit detail',
      };
    }
    return { success: true, data: mapDetail(payload.data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load acknowledgement audit detail',
    };
  }
}

export async function getAcknowledgementAuditTrailTimeline(
  id: number,
  facilityId: number,
  params: {
    limit?: number;
    offset?: number;
    sortOrder?: 'ASC' | 'DESC';
    startDate?: string;
    endDate?: string;
    status?: string;
  } = {}
): Promise<{
  success: boolean;
  data: AuditTimelineEntry[];
  count: number;
  error?: string;
}> {
  try {
    const url = `${API_ENDPOINTS.ACKNOWLEDGEMENT_AUDIT_TRAIL.TIMELINE(id)}${buildQueryString({
      facilityId,
      limit: params.limit,
      offset: params.offset,
      sortOrder: params.sortOrder ?? 'ASC',
      startDate: params.startDate,
      endDate: params.endDate,
      status: params.status,
    })}`;
    const response = await apiGet<ApiEnvelope<Record<string, unknown>[]>>(url, {
      requireAuth: true,
    });
    const payload = response.data;
    if (payload?.success === false) {
      return {
        success: false,
        data: [],
        count: 0,
        error: payload.message || 'Failed to load audit timeline',
      };
    }
    const rows = Array.isArray(payload?.data) ? payload.data.map(row => mapTimelineEntry(row)) : [];
    return {
      success: true,
      data: rows,
      count: Number(payload?.count ?? rows.length),
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Failed to load audit timeline',
    };
  }
}

export async function countAcknowledgementAuditTrailEvents(
  id: number,
  facilityId: number
): Promise<number | null> {
  const result = await getAcknowledgementAuditTrailTimeline(id, facilityId, {
    limit: 1,
    offset: 0,
    sortOrder: 'ASC',
  });
  return result.success ? result.count : null;
}

export async function exportAcknowledgementAuditTrail(
  format: AuditTrailExportFormat,
  filters: AuditTrailSearchFilters & {
    sortBy?: AuditTrailSortField;
    sortOrder?: 'ASC' | 'DESC';
  }
): Promise<void> {
  const url = `${API_ENDPOINTS.ACKNOWLEDGEMENT_AUDIT_TRAIL.EXPORT}${buildQueryString({
    facilityId: filters.facilityId,
    patientId: filters.patientId,
    studyId: filters.studyId,
    clinician: filters.clinician,
    criticalityBand: filters.criticalityBand,
    acknowledgementStatus: filters.acknowledgementStatus,
    startDate: filters.startDate,
    endDate: filters.endDate,
    search: filters.search,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    format,
  })}`;
  const authHeader = authService.getAuthHeader();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept:
        'text/csv, application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
      'tenant-id': API_CONFIG.TENANT_ID,
      ...(authHeader.Authorization ? { Authorization: authHeader.Authorization } : {}),
    },
    mode: 'cors',
  });

  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const json = await response.json();
      message = json?.message || json?.error || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename =
    match?.[1] || `acknowledgement-audit-trail.${format === 'excel' ? 'xlsx' : format}`;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
